import { useEffect, useState } from "react";
import { Bell, AlertTriangle, CalendarClock, FileText, Layers, Briefcase, Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import api from "@/lib/api";

const SEV_BADGE = {
  critical: "bg-red-600 text-white",
  high: "bg-amber-500 text-white",
  medium: "bg-yellow-100 text-yellow-800 border border-yellow-300",
  low: "bg-slate-100 text-slate-700 border border-slate-200",
};

const KIND_LINK = {
  npd: (id) => `/npd/${id}`,
  assembly: (id) => `/assemblies/${id}`,
  project: (id) => `/projects/${id}`,
  rfq: (id) => `/rfqs/${id}`,
};

const KIND_ICON = {
  npd: FileText,
  assembly: Layers,
  project: Briefcase,
  rfq: Inbox,
};

/**
 * R30 Phase B — Due-date alerts bell.
 * Shows a count badge in the sidebar header and opens a popover listing all
 * items the current user is responsible for, sorted by urgency.
 */
export default function AlertsBell({ collapsed = false }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ critical: 0, high: 0, medium: 0, low: 0 });
  const [open, setOpen] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const fetchAlerts = async () => {
    try {
      const r = await api.get("/alerts/me");
      setItems(r.data.items || []);
      setCounts(r.data.counts || { critical: 0, high: 0, medium: 0, low: 0 });
      setFetchError(false);
    } catch (e) {
      // Don't clear stale items — keep them visible so managers see the
      // last-known state. Flag staleness with the amber dot indicator.
      // eslint-disable-next-line no-console
      console.warn("AlertsBell fetch failed:", e?.response?.status, e?.message);
      setFetchError(true);
    }
  };

  useEffect(() => {
    fetchAlerts();
    const t = setInterval(fetchAlerts, 90 * 1000);  // refresh every 90s
    return () => clearInterval(t);
  }, []);

  const total = counts.critical + counts.high + counts.medium + counts.low;
  const urgent = counts.critical + counts.high;
  const badgeColor = counts.critical > 0
    ? "bg-red-600"
    : counts.high > 0
    ? "bg-amber-500"
    : "bg-slate-500";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`relative w-full flex items-center rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors ${
            collapsed ? "justify-center px-0 py-2" : "justify-between gap-2 px-3 py-2"
          }`}
          data-testid="alerts-bell"
          aria-label="Open due-date alerts"
          title={collapsed ? (total > 0 ? `Alerts (${total})` : "Alerts") : undefined}
        >
          <span className={`relative flex items-center ${collapsed ? "" : "gap-2.5"}`}>
            <Bell size={16} />
            {!collapsed && <span>Alerts</span>}
            {collapsed && total > 0 && (
              <span
                className={`absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full ${badgeColor} text-white text-[10px] font-bold flex items-center justify-center`}
                data-testid="alerts-count-badge"
              >
                {total > 9 ? "9+" : total}
              </span>
            )}
          </span>
          {!collapsed && total > 0 && (
            <span
              className={`min-w-[20px] h-5 px-1.5 rounded-full ${badgeColor} text-white text-[11px] font-bold flex items-center justify-center`}
              data-testid="alerts-count-badge"
            >
              {total > 99 ? "99+" : total}
            </span>
          )}
          {fetchError && (
            <span
              className={`absolute w-2 h-2 rounded-full bg-amber-400 ring-2 ring-slate-900 ${
                collapsed ? "top-0 right-1" : "top-0.5 right-1"
              }`}
              data-testid="alerts-stale-indicator"
              title="Could not refresh alerts — showing last known. Will retry shortly."
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        className="w-[360px] p-0 border-slate-200"
        data-testid="alerts-popover"
      >
        <div className="px-3 py-2.5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-600" />
            <span className="text-sm font-semibold text-slate-900">Due-Date Alerts</span>
          </div>
          <div className="flex items-center gap-1 text-[11px]">
            {counts.critical > 0 && <span className={`px-1.5 py-0.5 rounded ${SEV_BADGE.critical}`}>{counts.critical} overdue</span>}
            {counts.high > 0 && <span className={`px-1.5 py-0.5 rounded ${SEV_BADGE.high}`}>{counts.high} ≤3d</span>}
          </div>
        </div>
        <div className="max-h-[480px] overflow-y-auto divide-y divide-slate-100">
          {items.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-slate-400">
              You're all caught up 🎯
            </div>
          )}
          {items.map((a, i) => {
            const Icon = KIND_ICON[a.kind] || FileText;
            const href = (KIND_LINK[a.kind] || (() => "#"))(a.id);
            const dueLabel = a.days_to < 0
              ? `${Math.abs(a.days_to)}d OVERDUE`
              : a.days_to === 0
              ? "DUE TODAY"
              : `${a.days_to}d left`;
            return (
              <Link
                key={`${a.kind}-${a.id}-${i}`}
                to={href}
                onClick={() => setOpen(false)}
                className="block px-3 py-2.5 hover:bg-slate-50 transition-colors"
                data-testid={`alert-item-${a.kind}-${a.id}`}
              >
                <div className="flex items-start gap-2">
                  <Icon size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{a.kind}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${SEV_BADGE[a.severity]}`}>
                        <CalendarClock size={9} className="inline mr-0.5" />
                        {dueLabel}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-slate-900 truncate">{a.title}</div>
                    {a.due_date && (
                      <div className="text-[11px] text-slate-500">Due {a.due_date}</div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        {urgent > 0 && (
          <div className="px-3 py-2 border-t border-slate-200 bg-amber-50 text-[11px] text-amber-900">
            {urgent} urgent {urgent === 1 ? "item" : "items"} need your attention.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
